import React, { useEffect } from "react";
import { Modal, message, Spin } from "antd";
import { IconOrdersAlertsSettings } from "../Layout/Icons";
import axios from "axios";
import { FixedSizeList as List } from "react-window";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

const ALERT_SETTINGS_URL = "/options/alerts";

const OrdersAlertsModal = ({
  visible,
  onCancel,
  alerts,
  refreshAlerts,
  onEdit,
}) => {
  const history = useHistory();
  const [rulesData, setRulesData] = React.useState([]);
  const [alertsList, setAlertsList] = React.useState([]);
  const [selectedGroup, setSelectedGroup] = React.useState(null);
  const [refreshing, setRefreshing] = React.useState(false);
  const [movedOrderIds, setMovedOrderIds] = React.useState([]);
  const canManageAlerts = useSelector(
    (state) => !!state.auth.user.user_role?.permissions?.["options/alerts"]
  );

  const dismissOrderAlert = async (id) => {
    try {
      setRefreshing(true);
      await axios.delete(`/api/v1/order_alerts/${id}`);
      message.success("Alert dismissed successfully");
      await refreshAlerts();
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to dismiss alert"
      );
    } finally {
      setRefreshing(false);
    }
  };

  const editOrderAlert = async (values) => {
    try {
      setRefreshing(true);
      await axios.put(`/api/v1/order_alerts/${values}`, {
        order_alert: {
          seen_at: Date.now(),
        },
      });
      message.success("Alert updated successfully");
      // await refreshAlerts();
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to update alert");
    } finally {
      setRefreshing(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await axios.get("/api/v1/alerts");
      setAlertsList(response.data.data);
    } catch (error) {
      console.error("Error fetching alerts:", error);
      message.error("Failed to fetch alerts");
    }
  };

  const fetchRules = async () => {
    try {
      const response = await axios.get("/api/v1/alerts/rules");
      const rulesData = response.data.data;

      const ruleOptions = Object.entries(rulesData).map(([key, value]) => ({
        value: key,
        label: value.title,
      }));

      setRulesData(ruleOptions);
    } catch (error) {
      console.error("Error fetching rules:", error);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  useEffect(() => {
    if (visible) fetchAlerts();
  }, [visible]);

  const openAlertSettings = () => {
    onCancel();
    history.push(ALERT_SETTINGS_URL);
  };

  const renderGroups = () => {
    const enrichedAlerts = alerts.map((orderAlert) => {
      const alert = alertsList.find((a) => a.id === orderAlert.alert_id);
      return {
        ...orderAlert,
        rule: alert?.rule,
        display_title: alert?.display_title,
      };
    });

    const groupedAlerts = enrichedAlerts.reduce((acc, alert) => {
      const key = alert.alert_id;
      if (!acc[key]) {
        acc[key] = { title: alert.display_title, rule: alert.rule, alerts: [] };
      }
      acc[key].alerts.push(alert);
      return acc;
    }, {});

    const groupedAlertsEntries = Object.entries(groupedAlerts);

    if (groupedAlertsEntries.length === 0) {
      return <div className="empty-list-container">
        <p style={{ margin: 0 }}>List is empty</p>
      </div>;
    }

    return groupedAlertsEntries.map(([alertId, group]) => {
      const ruleTitle =
        group.title ||
        rulesData.find((r) => r.value === group.rule)?.label ||
        group.rule;
      return (
        <div
          key={alertId}
          className={`OrdersAlertsModal-groupItem ${selectedGroup === alertId ? "OrdersAlertsModal-groupItem-active" : ""}`}
          onClick={() =>
            setSelectedGroup(selectedGroup === alertId ? null : alertId)
          }
          style={{ cursor: "pointer" }}
        >
          <p className="OrdersAlertsModal-groupItemText">{ruleTitle}</p>
          <div className="OrdersAlertsModal-groupItemBadge">
            <p className="OrdersAlertsModal-groupItemBadgeText">
              {group.alerts.length}
            </p>
          </div>
        </div>
      );
    });
  };

  const filteredAlerts = React.useMemo(() => {
    const sortedAlerts = [...alerts].sort((a, b) => {
      if (movedOrderIds.includes(a.id) && !movedOrderIds.includes(b.id)) {
        return 1;
      }
      if (!movedOrderIds.includes(a.id) && movedOrderIds.includes(b.id)) {
        return -1;
      }
      return 0;
    });

    if (!selectedGroup) return sortedAlerts;

    return sortedAlerts.filter(
      (orderAlert) => String(orderAlert.alert_id) === String(selectedGroup)
    );
  }, [alerts, selectedGroup, movedOrderIds]);

  const AlertRow = ({ index, style, data }) => {
    const alert = data[index];
    const isMoved = movedOrderIds.includes(alert.id);
    const seenAtDate = new Date().toISOString();

    return (
      <div style={style}>
        <div
          key={alert.id}
          className={
            alert.seen_at || isMoved
              ? "OrdersAlertsModal-order"
              : "OrdersAlertsModal-order-active"
          }
          onClick={() => {
            if (!alert.seen_at) {
              editOrderAlert(alert.id);
              setMovedOrderIds((prev) => [...prev, alert.id]);
            }
          }}
        >
          <div className="OrdersAlertsModal-orderHeader">
            <div className="OrdersAlertsModal-orderHeaderTitle">
              <div
                className={
                  alert.seen_at || isMoved
                    ? "OrdersAlertsModal-orderHeaderTitleIcon"
                    : "OrdersAlertsModal-orderHeaderTitleIcon-active"
                }
              />
              <p
                onClick={(e) => {
                  e.stopPropagation();
                  editOrderAlert(alert.id);
                  setMovedOrderIds((prev) => [...prev, alert.id]);
                  onEdit(null, alert.order_id);
                  onCancel();
                }}
                className="OrdersAlertsModal-orderHeaderTitleText"
              >
                {alert.title}
              </p>
            </div>
            <div>
              <p
                className="OrdersAlertsModal-orderHeaderDismiss"
              >
                Dismiss
              </p>
            </div>
          </div>
          <div className="OrdersAlertsModal-orderBody">
            <p className="OrdersAlertsModal-orderBodyText">{alert.title}</p>
          </div>
          <div>
            <p className="OrdersAlertsModal-orderFooter">
              {isMoved ? seenAtDate : new Date(alert.created_at).toLocaleString()}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      title={
        <div className="OrdersAlertsModal-header">
          <p className="OrdersAlertsModal-headerTitle">Orders alerts</p>
          {canManageAlerts && (
            <div
              onClick={openAlertSettings}
              className="cursor-pointer d-flex"
              style={{ marginLeft: 5 }}
              title="Alert settings"
            >
              <IconOrdersAlertsSettings />
            </div>
          )}
        </div>
      }
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
      bodyStyle={{ padding: "14px 24px" }}
    >
      <div>
        <div>
          <p className="OrdersAlertsModal-title">Groups</p>
          <div className="OrdersAlertsModal-group">{renderGroups()}</div>
        </div>
        <div>
          <p className="OrdersAlertsModal-title">Orders</p>
          <div style={{ height: "50vh" }}>
            {refreshing ? (
              <div className="d-flex justify-content-center align-items-center h-100">
                <Spin size="large" />
              </div>
            ) : (
              filteredAlerts.length === 0 ? (
                <div className="empty-list-container">
                  <p style={{ margin: 0 }}>List is empty</p>
                </div>
              ) : (
                <List
                  height={window.innerHeight * 0.5}
                  itemCount={filteredAlerts.length}
                  itemSize={100}
                  width="100%"
                  itemData={filteredAlerts}
                >
                  {AlertRow}
                </List>
              )
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default OrdersAlertsModal;
